import http from "./http.service";
import Promisable from "./promisable.service";
import { getAppDispatch } from "utils/dispatch.util";
import { assetManagementActions } from "redux/slices/assetManagement/assetManagementSlice";
import { modalActions } from "redux/slices/modal";
import { NavigateFunction } from "react-router-dom";

const url = "/asset-management";

const AssetManagementService = {

    uploadImages: async (files: File[]): Promise<string[]> => {
        if (files.length === 0) return [];
        
        http.setJWT();
        http.setMultiPart();
        
        const formData = new FormData();
        files.forEach((file) => {
            formData.append("images", file);
        });
        
        const [success, error]: any = await Promisable.asPromise(
            http.post(`${url}/e-cars/upload-images`, formData)
        );
        
        if (success && success.data?.data?.images) {
            return success.data.data.images;
        } else if (success && success.data?.images) {
            return success.data.images;
        } else if (error) {
            console.error("Image upload failed:", error);
            throw error;
        }
        
        return [];
    },

    getAssetCategories: async () => {
        const dispatch = getAppDispatch();
        http.setJWT();
        const [success, error]: any = await Promisable.asPromise(
            http.get(`${url}/category`)
        );
        if (success) {
            const { categories } = success.data.data || {};
            dispatch?.(assetManagementActions.setAssetCategories(categories || []));
        }
        return [success, error];
    },

    getGroupedAssetsSelection: async (filters: any = {}) => {
        const dispatch = getAppDispatch();
        dispatch?.(assetManagementActions.setLoading(true));
        http.setJWT();

        const [success, error]: any = await Promisable.asPromise(
            http.post(`${url}/grouped`, filters)
        );
        if (success) {
            const { assetSelectionData, count } = success.data.data || {};
            dispatch?.(assetManagementActions.setHandoverData({ handoverData: assetSelectionData || [], count: count || 0 }));
        }
        dispatch?.(assetManagementActions.setLoading(false));
        return [success, error];
    },
    addAssetCategory: async (data: any, filters?: any) => {
        const dispatch = getAppDispatch();
        dispatch?.(modalActions.setLoading(true));
        http.setJWT();
        const [success, error]: any = await Promisable.asPromise(
            http.post(`${url}/category`, data)
        );
        if (success) {
            await AssetManagementService.getGroupedAssetsSelection(filters);
            dispatch?.(modalActions.closeModal());
        }
        dispatch?.(modalActions.setLoading(false));
        return [success, error];
    },
    addAssetSubCategory: async (data: any, filters?: any) => {
        const dispatch = getAppDispatch();
        dispatch?.(modalActions.setLoading(true));
        http.setJWT();
        const [success, error]: any = await Promisable.asPromise(
            http.post(`${url}/subcategory`, data)
        );
        if (success) {
            await AssetManagementService.getGroupedAssetsSelection(filters);
            dispatch?.(modalActions.closeModal());
        }
        dispatch?.(modalActions.setLoading(false));
        return [success, error];
    },
    updateAssetCategory: async (id: string, data: any, filters?: any) => {
        const dispatch = getAppDispatch();
        dispatch?.(modalActions.setLoading(true));
        http.setJWT();
        const [success, error]: any = await Promisable.asPromise(
            http.patch(`${url}/category/${id}`, data)
        );
        if (success) {
            await AssetManagementService.getGroupedAssetsSelection(filters);
            dispatch?.(modalActions.closeModal());
        }
        dispatch?.(modalActions.setLoading(false));
        return [success, error];
    },
    updateAssetSubCategory: async (id: string, data: any, filters?: any) => {
        const dispatch = getAppDispatch();
        dispatch?.(modalActions.setLoading(true));
        http.setJWT();
        const [success, error]: any = await Promisable.asPromise(
            http.patch(`${url}/subcategory/${id}`, data)
        );
        if (success) {
            await AssetManagementService.getGroupedAssetsSelection(filters);
            dispatch?.(modalActions.closeModal());
        }
        dispatch?.(modalActions.setLoading(false));
        return [success, error];
    },
    // Asset Listing
    getAssetListing: async (filters?: any) => {
        const dispatch = getAppDispatch();
        dispatch?.(assetManagementActions.setLoading(true));
        http.setJWT();


        const [success, error]: any = await Promisable.asPromise(
            http.post(`${url}/handover-items`, filters)
        );
        if (success) {
            const { items, count } = success.data.data || {};
            dispatch?.(assetManagementActions.setAssetListingData({
                assetListingData: items || [],
                count: count || 0
            }));
        }
        dispatch?.(assetManagementActions.setLoading(false));
        return [success, error];
    },
    addAsset: async (formData: FormData, navigate?: NavigateFunction) => {
        const dispatch = getAppDispatch();
        dispatch?.(assetManagementActions.setLoading(true));
        http.setJWT();
        const [success, error]: any = await Promisable.asPromise(
            http.post(`${url}/handover-items/add`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
        );
        if (success) {
            dispatch?.(assetManagementActions.refresh());
            navigate?.("/asset-management?tab=asset-listing");
        }
        dispatch?.(assetManagementActions.setLoading(false));
        return [success, error];
    },
    returnAsset: async (id: string, formData: FormData, filters?: any) => {
        const dispatch = getAppDispatch();
        dispatch?.(modalActions.setLoading(true));
        http.setJWT();
        const [success, error]: any = await Promisable.asPromise(
            http.patch(`${url}/handover-items/${id}/return`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
        );
        if (success) {
            await AssetManagementService.getAssetListing(filters);
            dispatch?.(modalActions.closeModal());
        }
        dispatch?.(modalActions.setLoading(false));
        return [success, error];
    },
    getAsset: async (id: string) => {
        const dispatch = getAppDispatch();
        dispatch?.(assetManagementActions.setLoading(true));
        http.setJWT();
        const [success, error]: any = await Promisable.asPromise(
            http.get(`${url}/handover-items/${id}`)
        );
        if (success) {
            const { item } = success.data.data || {};
            dispatch?.(assetManagementActions.setCurrentAsset(item || null));
        }
        dispatch?.(assetManagementActions.setLoading(false));
        return [success, error];
    },
    updateAsset: async (id: string, data: any, navigate?: NavigateFunction) => {
        const dispatch = getAppDispatch();
        dispatch?.(assetManagementActions.setLoading(true));
        http.setJWT();
        const [success, error]: any = await Promisable.asPromise(
            http.patch(`${url}/handover-items/${id}`, data)
        );
        if (success) {
            dispatch?.(assetManagementActions.refresh());
            navigate?.("/asset-management?tab=asset-listing");
        }
        dispatch?.(assetManagementActions.setLoading(false));
        return [success, error];
    },
    // E Cars Listing
    getECarsListing: async (filters?: any) => {
        const dispatch = getAppDispatch();
        dispatch?.(assetManagementActions.setLoading(true));
        http.setJWT();

        const [success, error]: any = await Promisable.asPromise(
            http.post(`${url}/e-cars`, filters)
        );
        if (success) {
            const { items, count } = success.data.data || {};
            dispatch?.(assetManagementActions.setECarsListingData({
                eCarsListingData: items || [],
                count: count || 0
            }));
        }
        dispatch?.(assetManagementActions.setLoading(false));
        return [success, error];
    },
    addECar: async (data: any, navigate?: NavigateFunction) => {
        const dispatch = getAppDispatch();
        dispatch?.(assetManagementActions.setLoading(true));
        http.setJWT();
        const [success, error]: any = await Promisable.asPromise(
            http.post(`${url}/e-cars/add`, data)
        );
        if (success) {
            dispatch?.(assetManagementActions.refreshECars());
            navigate?.("/asset-management?tab=e-cars");
        }
        dispatch?.(assetManagementActions.setLoading(false));
        return [success, error];
    },
    getECar: async (id: string) => {
        const dispatch = getAppDispatch();
        dispatch?.(assetManagementActions.setLoading(true));
        http.setJWT();
        const [success, error]: any = await Promisable.asPromise(
            http.get(`${url}/e-cars/${id}`)
        );
        if (success) {
            const { item } = success.data.data || {};
            dispatch?.(assetManagementActions.setCurrentECar(item || null));
        }
        dispatch?.(assetManagementActions.setLoading(false));
        return [success, error];
    },
    updateECar: async (id: string, data: any, navigate?: NavigateFunction) => {
        const dispatch = getAppDispatch();
        dispatch?.(assetManagementActions.setLoading(true));
        http.setJWT();
        const isFormData = data instanceof FormData;
        const [success, error]: any = await Promisable.asPromise(
            http.patch(`${url}/e-cars/${id}`, data, isFormData ? {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            } : {})
        );
        if (success) {
            dispatch?.(assetManagementActions.refreshECars());
            navigate?.("/asset-management?tab=e-cars");
        }
        dispatch?.(assetManagementActions.setLoading(false));
        return [success, error];
    },
    returnECar: async (id: string, data: any, filters?: any) => {
        const dispatch = getAppDispatch();
        dispatch?.(modalActions.setLoading(true));
        http.setJWT();
        const [success, error]: any = await Promisable.asPromise(
            http.patch(`${url}/e-cars/${id}/return`, data)
        );
        if (success) {
            await AssetManagementService.getECarsListing(filters);
            dispatch?.(modalActions.closeModal());
        }
        dispatch?.(modalActions.setLoading(false));
        return [success, error];
    },
};

export default AssetManagementService;

